// FinGate — the second-password gate in front of the Financials app.
// States: loading → no-access | create-passphrase | unlock | locked-out.
// Mounted at the FinancialsApp root so every tab sits behind it. Matches
// mockups/financials-v1.html (approved 2026-08-25).
import React, { useEffect, useState } from "react";
import { C, font } from "../../lib/styles.js";
import { finSetupPassphrase, finStatus, finUnlock } from "../../lib/qboClient.js";
import { setUnlock } from "./lib/finSession.js";

const wrap = {
  maxWidth: 360, margin: "60px auto", textAlign: "center", fontFamily: font,
};
const glyphBox = {
  width: 52, height: 52, margin: "0 auto 14px", borderRadius: 12,
  background: C.surfaceAlt, display: "flex", alignItems: "center",
  justifyContent: "center", fontSize: 22,
};
const h3 = { fontSize: 16, fontWeight: 500, marginBottom: 6, color: C.text };
const p = { fontSize: 12.5, color: C.muted, lineHeight: 1.5, marginBottom: 14 };
const inputStyle = {
  width: "100%", padding: "10px 13px", background: C.surface,
  border: `1px solid ${C.border}`, borderRadius: 6, font: "inherit",
  fontSize: 13, marginBottom: 10, boxSizing: "border-box",
};
const primaryBtn = {
  width: "100%", fontSize: 13, padding: "9px 13px", borderRadius: 6,
  border: `1px solid ${C.text}`, background: C.text, color: "#fff",
  cursor: "pointer", fontFamily: font,
};
const errStyle = { color: C.danger, fontSize: 12, marginBottom: 8 };

const LockGlyph = () => (
  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke={C.brown} strokeWidth="1.5">
    <rect x="5" y="10" width="14" height="10" rx="2" />
    <path d="M8 10V7a4 4 0 0 1 8 0v3" />
  </svg>
);

const fmtCountdown = (ms) => {
  const s = Math.max(0, Math.ceil(ms / 1000));
  return `${Math.floor(s / 60)}:${String(s % 60).padStart(2, "0")}`;
};

/**
 * Renders the gate; calls onUnlocked({ token, expiresAt, status }) once a live
 * unlock exists. `user` is the appUser (uid via _docId).
 */
export default function FinGate({ onUnlocked }) {
  const [status, setStatus] = useState(null); // finStatus result
  const [statusError, setStatusError] = useState(null);
  const [pass, setPass] = useState("");
  const [pass2, setPass2] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  const [lockedUntil, setLockedUntil] = useState(null);
  const [now, setNow] = useState(Date.now());

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const s = await finStatus();
        if (!cancelled) {
          setStatus(s);
          if (s.lockedUntil) setLockedUntil(s.lockedUntil);
        }
      } catch (e) {
        if (!cancelled) setStatusError(e.message);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  // Lockout countdown tick.
  useEffect(() => {
    if (!lockedUntil) return undefined;
    const id = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(id);
  }, [lockedUntil]);
  const lockedLeft = lockedUntil ? lockedUntil - now : 0;
  const isLocked = lockedLeft > 0;

  const doUnlock = async () => {
    if (!pass || busy) return;
    setBusy(true); setError(null);
    try {
      const { token, expiresAt } = await finUnlock({ passphrase: pass });
      setUnlock(token, expiresAt);
      setPass("");
      onUnlocked({ token, expiresAt, status });
    } catch (e) {
      if (e.details?.lockedUntil) {
        setLockedUntil(e.details.lockedUntil);
      } else if (e.details?.attemptsRemaining !== undefined) {
        setError(`Incorrect passphrase — ${e.details.attemptsRemaining} attempt${e.details.attemptsRemaining === 1 ? "" : "s"} remaining`);
      } else {
        setError(e.message);
      }
    } finally {
      setBusy(false);
    }
  };

  const doCreate = async () => {
    if (busy) return;
    if (pass.length < 10) { setError("Passphrase must be at least 10 characters"); return; }
    if (pass !== pass2) { setError("Passphrases don't match"); return; }
    setBusy(true); setError(null);
    try {
      await finSetupPassphrase({ passphrase: pass });
    } catch (e) {
      setError(e.message);
      setBusy(false);
      return;
    }
    // Setup committed — flip local status FIRST so a transient failure of the
    // follow-on unlock lands on the normal unlock form, never back on the
    // create screen (where retrying would be rejected — codex review).
    setStatus((s) => ({ ...s, passphraseSet: true }));
    try {
      const { token, expiresAt } = await finUnlock({ passphrase: pass });
      setUnlock(token, expiresAt);
      setPass(""); setPass2("");
      onUnlocked({ token, expiresAt, status: { ...status, passphraseSet: true } });
    } catch (e) {
      setPass(""); setPass2("");
      setError(e.message);
    } finally {
      setBusy(false);
    }
  };

  if (statusError) {
    return (
      <div style={wrap}>
        <div style={glyphBox}>⚠️</div>
        <h3 style={h3}>Couldn&apos;t check access</h3>
        <p style={p}>{statusError}</p>
      </div>
    );
  }
  if (!status) {
    return <div style={{ ...wrap, color: C.muted, fontSize: 13 }}>Checking access…</div>;
  }

  if (!status.granted) {
    return (
      <div style={wrap}>
        <div style={glyphBox}>🚫</div>
        <h3 style={h3}>No financials access</h3>
        <p style={p}>Your account doesn&apos;t have access to the Financials area. Ask Ivin if you need it.</p>
      </div>
    );
  }

  if (isLocked) {
    return (
      <div style={wrap}>
        <div style={glyphBox}>⏳</div>
        <h3 style={h3}>Too many attempts</h3>
        <p style={p}>
          Financials is locked for <span style={{ fontFamily: "monospace" }}>{fmtCountdown(lockedLeft)}</span> after
          repeated incorrect tries. Every attempt is logged.
        </p>
      </div>
    );
  }

  if (!status.passphraseSet) {
    return (
      <div style={wrap}>
        <div style={glyphBox}>🔐</div>
        <h3 style={h3}>Create your financials passphrase</h3>
        <p style={p}>You&apos;ve been granted access. Choose a passphrase (10+ characters) — you&apos;ll enter it each time you open Financials.</p>
        {error && <div style={errStyle}>{error}</div>}
        <input style={inputStyle} type="password" placeholder="New passphrase" value={pass}
          onChange={(e) => setPass(e.target.value)} autoComplete="new-password" />
        <input style={inputStyle} type="password" placeholder="Repeat passphrase" value={pass2}
          onChange={(e) => setPass2(e.target.value)} autoComplete="new-password"
          onKeyDown={(e) => e.key === "Enter" && doCreate()} />
        <button style={{ ...primaryBtn, opacity: busy ? 0.6 : 1 }} onClick={doCreate} disabled={busy}>
          {busy ? "Setting…" : "Set passphrase"}
        </button>
      </div>
    );
  }

  return (
    <div style={wrap}>
      <div style={glyphBox}><LockGlyph /></div>
      <h3 style={h3}>Financials is locked</h3>
      <p style={p}>Enter your financials passphrase. This is separate from your Suite sign-in.</p>
      {error && <div style={errStyle}>{error}</div>}
      <input style={inputStyle} type="password" placeholder="Passphrase" value={pass}
        onChange={(e) => setPass(e.target.value)} autoComplete="current-password" autoFocus
        onKeyDown={(e) => e.key === "Enter" && doUnlock()} />
      <button style={{ ...primaryBtn, opacity: busy ? 0.6 : 1 }} onClick={doUnlock} disabled={busy}>
        {busy ? "Unlocking…" : "Unlock for 15 minutes"}
      </button>
    </div>
  );
}
